import { useCallback, useMemo, useState } from 'react';
import { graphql } from 'react-relay';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import {
	buildQueryVariables,
	ENTIRE_MODE_PAGE_SIZE,
	getMaxResults,
} from '../utils/blogPostDataUtils.ts';
import type { BlogPostsMacroParams } from '../blogPostMacroTypes.ts';

import type {
	useBlogPostsQueryQuery$data,
	useBlogPostsQueryQuery$variables,
} from './__generated__/useBlogPostsQueryQuery.graphql.ts';

type UseBlogPostsQueryResult = {
	data: useBlogPostsQueryQuery$data | undefined;
	error?: Error;
	loading: boolean;
	cql: string;
	hasNextPage?: boolean;
	isLoadingMore?: boolean;
	loadMore?: () => Promise<void>;
	loadMoreError?: Error;
};

export const useBlogPostsQuery = (
	params: BlogPostsMacroParams,
	currentSpaceKey?: string,
): UseBlogPostsQueryResult => {
	const variables = useMemo(
		() => buildQueryVariables(params, currentSpaceKey),
		[currentSpaceKey, params],
	);
	const maxResults = getMaxResults(params);

	const [loadMoreError, setLoadMoreError] = useState<Error | undefined>();
	const [isLoadingMore, setIsLoadingMore] = useState(false);

	const { data, error, loading, fetchMore } = useQueryInterop<
		useBlogPostsQueryQuery$data,
		useBlogPostsQueryQuery$variables
	>(
		graphql`
			query useBlogPostsQueryQuery(
				$after: String
				$cql: String!
				$first: Int
				$includeEntireBody: Boolean!
				$includeExcerpt: Boolean!
				$includeAdfMediaToken: Boolean!
			) {
				search: confluence_search(cql: $cql, first: $first, after: $after) {
					count
					nodes {
						content {
							id
							title
							type
							excerpt(length: 500) @include(if: $includeExcerpt)
							macroExcerpt @include(if: $includeExcerpt) {
								atlasDocFormat(fallbackToBody: false)
							}
							body {
								atlas_doc_format @include(if: $includeAdfMediaToken) {
									value @include(if: $includeEntireBody)
									mediaToken {
										token
									}
								}
							}
							labels {
								nodes {
									name
								}
							}
							links {
								webui
								base
							}
							space {
								id
								key
								name
							}
							history {
								createdBy {
									... on KnownUser {
										accountId
										displayName
										profilePicture {
											path
										}
									}
									... on UnknownUser {
										displayName
										profilePicture {
											path
										}
									}
								}
								createdDate
								lastUpdated {
									when
								}
							}
						}
					}
					pageInfo {
						endCursor
						hasNextPage
					}
				}
			}
		`,
		{ variables, notifyOnNetworkStatusChange: true, fetchPolicy: 'cache-first' },
	);

	const loadedCount = data?.search?.nodes?.length ?? 0;
	const remainingCount = Math.max(maxResults - loadedCount, 0);
	const pageInfo = data?.search?.pageInfo;
	const isInitialLoading = loading && !data?.search;
	const canLoadMore =
		variables.includeEntireBody &&
		remainingCount > 0 &&
		Boolean(pageInfo?.hasNextPage) &&
		Boolean(pageInfo?.endCursor);

	const loadMore = useCallback(async (): Promise<void> => {
		const endCursor = pageInfo?.endCursor;
		if (!canLoadMore || !endCursor || loading || isLoadingMore) {
			return;
		}

		setLoadMoreError(undefined);
		setIsLoadingMore(true);

		await fetchMore({
			variables: {
				after: endCursor,
				first: Math.min(ENTIRE_MODE_PAGE_SIZE, remainingCount),
			},
			updateQuery: (prev, { fetchMoreResult }) => {
				if (!fetchMoreResult?.search) {
					return prev;
				}

				return {
					...prev,
					search: {
						...(prev.search ?? {}),
						count: fetchMoreResult.search.count ?? prev.search?.count ?? null,
						nodes: [...(prev.search?.nodes ?? []), ...(fetchMoreResult.search.nodes ?? [])],
						pageInfo: fetchMoreResult.search.pageInfo,
					},
				};
			},
		})
			.catch((err) => {
				setLoadMoreError(err instanceof Error ? err : new Error(String(err)));
			})
			.finally(() => {
				setIsLoadingMore(false);
			});
	}, [canLoadMore, fetchMore, isLoadingMore, loading, pageInfo?.endCursor, remainingCount]);

	return {
		data,
		error,
		loading: isInitialLoading,
		cql: variables.cql,
		hasNextPage: canLoadMore,
		isLoadingMore,
		loadMore: canLoadMore ? loadMore : undefined,
		loadMoreError,
	};
};
